import { Link } from "react-router-dom";
import PhygitalIcon from "@/components/PhygitalIcon";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { getCategoryPreset, CATEGORY_ORDER } from "../lib/categoryConfig";

const SharedFooter = ({ variant = "light" }) => {
  const isDark = variant === "dark";
  const { settings } = useCompanySettings();
  const companyName = settings.company_name || "Phygital";
  const tagline = settings.tagline || "Where Physical Meets Digital";
  const legalName = settings.legal_entity_name || `${companyName} Systems LLC`;
  const socialLinks = settings.social_links || {};
  
  const DEMO_ROUTES = { hair: "/hair", nails: "/nail", makeup: "/mua", lashes: "/lash", events: "/events", wellness: "/wellness", culinary: "/culinary", instructors: "/instructor", travel: "/travel", dispensary: "/dispensaries", retail: "/retail" };
  const COMING_SOON = ["travel", "dispensary", "style"];
  const demos = CATEGORY_ORDER.map(key => {
    const preset = getCategoryPreset(key);
    return { path: DEMO_ROUTES[key] || `/${key}`, label: preset.name, icon: preset.icon, iconColor: preset.iconColor, bgColor: preset.bgColor, comingSoon: COMING_SOON.includes(key) };
  });

  const activeSocials = Object.entries(socialLinks).filter(([key, val]) => val && val.trim() && key !== "website");
  
  return (
    <footer className={isDark ? "bg-gray-900" : "bg-gray-50 border-t border-gray-100"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col items-center gap-5">
          {/* Logo - Clickable to home */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <PhygitalIcon className="w-8 h-8" variant={isDark ? "light" : "dark"} />
            <span className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>{companyName.toLowerCase()}</span>
          </Link>
          
          {/* Tagline */}
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} text-center max-w-md`}>
            {tagline}
          </p>

          {/* Demos Row */}
          <div className="flex flex-col items-center gap-2">
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Demos:</span>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {demos.map((demo) => {
                const Icon = demo.icon;
                return (
                  <Link
                    key={demo.path}
                    to={demo.path}
                    onClick={() => window.scrollTo(0, 0)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors`}
                    style={isDark
                      ? { backgroundColor: `${demo.iconColor}15`, color: demo.iconColor }
                      : { backgroundColor: demo.bgColor, color: demo.iconColor }
                    }
                  >
                    <Icon className="w-3 h-3" style={{ color: demo.iconColor }} />
                    {demo.label}
                    {demo.comingSoon && (
                      <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(Soon)</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Social Links */}
          {activeSocials.length > 0 && (
            <div className="flex items-center justify-center gap-4">
              {activeSocials.map(([platform, url]) => (
                <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className={`text-xs capitalize hover:underline ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}>
                  {platform}
                </a>
              ))}
            </div>
          )}

          {/* Links */}
          <div className={`flex items-center justify-center gap-6 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <Link to="/help" className={`hover:${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
              Help
            </Link>
            <Link to="/privacy" className={`hover:${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
              Privacy
            </Link>
            <Link to="/terms" className={`hover:${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
              Terms
            </Link>
          </div>

          {/* Copyright */}
          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} pt-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'} w-full text-center`}>
            &copy; {new Date().getFullYear()} {legalName}. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SharedFooter;
